import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
} from 'react';
import { Upload } from './universe';
import { Input, MessagePlugin, Button } from 'tdesign-react';
import { useMenuStore, useImageStore } from '../../store';
import { isLogin } from '../../utils/index.js';
import {
  DEFAULT_ACCEPT_LIST,
  DEFAULT_IMAGE_PREFIX,
  DEFAULT_MAX_FILE_SIZE,
  MESSAGE_DURATION,
  DEFAULT_MESSAGES,
  REGEX,
} from '../../const/constants.js';

const URL_EXP = REGEX.URL;

/**
 * 将 File 转为 base64
 * @param {File} file
 * @returns {Promise<string>}
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function showErrorMsg(msg) {
  MessagePlugin.error({
    content: msg,
    duration: MESSAGE_DURATION,
    className: 'ocrDemo-messgae-error',
    icon: <span className="ocrDemo-icon-errorMsg" />,
  });
}

function getFileExt(name = '') {
  return name.split('.').pop().toLowerCase();
}

function stripBase64Prefix(base64, ext) {
  if (!base64) return base64;
  // bmp 需剥离 data url 前缀，不同系统给出 MIME 可能是
  // image/bmp 或 image/x-ms-bmp，统一按 image/* 前缀处理
  if (ext === 'bmp') return base64.replace(/^data:image\/[^;]+;base64,/, '');
  if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'md', 'txt', 'pdf'].includes(ext)) {
    return base64.replace(/data.+?;base64,/, '');
  }
  return base64;
}

/**
 * 多图上传入口：上传本地文件按钮 + URL 输入
 * 多图菜单下替代单图上传组件，两个 tab 都展示
 */
export function MultiUploadDropzone({ cRef }) {
  const currentMenu = useMenuStore((s) => s.currentMenu);
  const multiUploadPages = useImageStore((s) => s.multiUploadPages);
  const pdfPreviewPages = useImageStore((s) => s.pdfPreviewPages);
  const pdfPreviewState = useImageStore((s) => s.pdfPreviewState);
  const setPdfPreviewPages = useImageStore((s) => s.setPdfPreviewPages);
  const addMultiUploadPages = useImageStore((s) => s.addMultiUploadPages);
  const setCurrentImage = useImageStore((s) => s.setCurrentImage);
  const setImgListType = useImageStore((s) => s.setImgListType);
  const setTotalPage = useImageStore((s) => s.setTotalPage);
  const setIsImageError = useImageStore((s) => s.setIsImageError);

  const acceptList = currentMenu?.acceptList || DEFAULT_ACCEPT_LIST;
  const prefix = currentMenu?.prefix || DEFAULT_IMAGE_PREFIX;
  const maxLength = currentMenu?.maxLength || DEFAULT_MAX_FILE_SIZE;

  const [imgUrl, setImgUrl] = useState('');
  const clickBtn = useRef();

  const acceptStr = acceptList.map((e) => `.${e}`).join(',');

  // 供外部（缩略图区的"+"上传入口）触发文件选择
  useImperativeHandle(cRef, () => ({
    onUpload: () => {
      clickBtn.current?.click();
    },
  }));

  // 页列表变化时，同步一个"合成的 currentImage"，供右侧参数面板和"开始识别"按钮使用
  // 同时把 tab 定位到「上传文件」
  useEffect(() => {
    if (!multiUploadPages || multiUploadPages.length === 0) return;
    const first = multiUploadPages[0];
    const listPrefix =
      first.type === 'url' ? `${prefix}UrlList` : `${prefix}Base64List`;
    // 图片页可直接用 <img> 预览；pdf 用「前端本地逐页渲染」的结果
    const previewablePages = multiUploadPages.filter(
      (p) => (p.fileType || '').toLowerCase() !== 'pdf'
    );
    const hasPdfPage = multiUploadPages.some(
      (p) => (p.fileType || '').toLowerCase() === 'pdf'
    );
    const pagePreviews =
      previewablePages.length > 0
        ? previewablePages.map((p, idx) => ({
            ImageUrl: p.previewUrl,
            pageNum: idx + 1,
          }))
        : (pdfPreviewPages || []).map((p, idx) => ({
            ImageUrl: p.ImageUrl,
            // 先带本地渲染尺寸，后端返回坐标框后会替换为后端坐标系宽高
            Width: p.Width,
            Height: p.Height,
            pageNum: idx + 1,
          }));
    setCurrentImage({
      isMultiUpload: true,
      multiPages: multiUploadPages,
      // 让预览侧的多页 Thumbnail 复用 pdfImgList 结构（每项含 ImageUrl）
      pdfImgList: pagePreviews,
      previewUrl: pagePreviews[0]?.ImageUrl || first.previewUrl,
      url: first.url,
      OriginUrl: first.OriginUrl,
      fileType: first.fileType,
      // prefix 仅用于 Request 展示（实际发送时按 List key 覆盖）
      prefix: first.prefix,
      // 供 GeneralBasicOCR Async 分支识别的 List key
      listPrefix,
      isExample: false,
      // PDF 本地渲染完成前先占位，渲染好后自动切到页图
      cantPreview: pagePreviews.length === 0,
      cantPreviewMsg:
        pagePreviews.length === 0 && hasPdfPage && previewablePages.length === 0
          ? pdfPreviewState === 'failed'
            ? 'PDF 页解析失败，可点击「抽取」后查看后端返回的页图'
            : '正在解析 PDF 页…'
          : '',
      type: first.type,
      pageNum: 1,
    });
    setTotalPage(multiUploadPages.length);
    setImgListType('upload');
    setIsImageError(false);
  }, [
    multiUploadPages,
    pdfPreviewPages,
    pdfPreviewState,
    prefix,
    setCurrentImage,
    setTotalPage,
    setImgListType,
    setIsImageError,
  ]);

  /**
   * 校验是否与已有页类型一致：拒绝跨类型（url/base64）混批
   * @param {Array} newPages 新增页
   * @returns {boolean}
   */
  const guardMixType = (newPages) => {
    if (!multiUploadPages || multiUploadPages.length === 0) return true;
    const existingType = multiUploadPages[0].type;
    const bad = newPages.some((p) => p.type !== existingType);
    if (bad) {
      showErrorMsg('同一次多图上传不支持混合 URL 和本地文件，请分别提交');
      return false;
    }
    return true;
  };

  const beforeUpload = (file) => {
    return new Promise(async (_, reject) => {
      try {
        await isLogin();
        if (!file) {
          showErrorMsg(DEFAULT_MESSAGES.SELECT_FILE);
          return reject();
        }
        const ext = getFileExt(file.name);
        if (!acceptList.includes(ext)) {
          showErrorMsg(
            currentMenu?.uploadExtErrorMsg ? (
              <>
                体验页面不支持{ext}格式，请点击
                <a
                  style={{ color: '#4A70FF' }}
                  target="_blank"
                  rel="noreferrer"
                  href={currentMenu.url}
                >
                  {currentMenu.title}
                </a>
                调用API接口
              </>
            ) : (
              DEFAULT_MESSAGES.INVALID_FILE_FORMAT
            )
          );
          return reject();
        }
        const base64 = await fileToBase64(file);
        if (base64?.length > maxLength * 1024 * 1024) {
          showErrorMsg(
            `${DEFAULT_MESSAGES.FILE_TOO_LARGE_BASE641}${maxLength}M${DEFAULT_MESSAGES.FILE_TOO_LARGE_BASE642}`
          );
          return reject();
        }
        const stripped = stripBase64Prefix(base64, ext);
        const newPage = {
          uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'base64',
          previewUrl: base64,
          OriginUrl: stripped,
          url: stripped,
          base64prefix: '',
          prefix: `${prefix}Base64`,
          fileType: ext,
          isExample: false,
          pageNum: 1,
          cantPreview: false,
          name: file.name,
          size: file.size,
        };
        if (!guardMixType([newPage])) {
          return reject();
        }
        addMultiUploadPages([newPage]);

        // PDF：动态引入 pdf.js（独立 chunk，不影响首屏），前端本地逐页渲染作为预览底图。
        // 不走后端返回的 ImageZipUrl —— 它落在别的业务 COS，浏览器直连会被跨域拦。
        if (ext === 'pdf') {
          setPdfPreviewPages([], 'loading');
          import('../../utils/pdfPreview.js')
            .then(({ renderPdfPagesToImages }) => renderPdfPagesToImages(base64))
            .then((pages) => {
              setPdfPreviewPages(pages, pages.length > 0 ? 'done' : 'failed');
            })
            .catch((error) => {
              console.warn('[PDF] 本地预览渲染失败:', error);
              setPdfPreviewPages([], 'failed');
            });
        }
      } catch (err) {
        // ignore
      }
      // 始终 reject 阻断 Upload 组件的默认 request
      return reject();
    });
  };

  const handleCheckUrl = () => {
    const url = imgUrl.trim();
    if (!URL_EXP.test(url)) {
      showErrorMsg(DEFAULT_MESSAGES.INVALID_URL);
      return;
    }
    const uploadExtList = currentMenu?.uploadExtList || [];
    const allExts = uploadExtList.concat(acceptList);
    const matched = allExts.filter((e) => url.toLowerCase().indexOf(e) > -1);
    const ext = matched.length > 0 ? matched[matched.length - 1] : '';
    if (!acceptList.includes(ext)) {
      showErrorMsg(DEFAULT_MESSAGES.INVALID_FILE_FORMAT);
      return;
    }
    const newPage = {
      uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: 'url',
      url,
      OriginUrl: url,
      previewUrl: url,
      prefix: `${prefix}Url`,
      fileType: ext,
      isExample: false,
      pageNum: 1,
      cantPreview: false,
    };
    if (!guardMixType([newPage])) return;
    addMultiUploadPages([newPage]);
    setImgUrl('');
  };

  const acceptText = acceptList.join('、');
  // 上传提示直接作为输入框占位文案展示
  const uploadHint = `支持上传1份多页PDF，或多张图片，或输入多个URL发起多页切题处理，支持${acceptText}等格式，大小不超过${maxLength}M`;

  return (
    <div className="ocr_upload__container">
      <div className="ocr_upload__width">
        <Upload multiple beforeUpload={beforeUpload} accept={acceptStr}>
          <Button type="button" variant="base" ref={clickBtn} width="full">
            上传本地文件
          </Button>
        </Upload>
        <div className="unv-upload__btn-extra">
          <span className="unv-upload__btn-extra__tips">或</span>
          <Input
            prefixIcon={<span className="ocrDemo-icon_search" />}
            suffixIcon={
              <span className="ocrDemo-icon_enter" onClick={handleCheckUrl} />
            }
            style={{ width: '100%' }}
            value={imgUrl}
            onEnter={handleCheckUrl}
            onChange={(v) => setImgUrl(v)}
            placeholder={uploadHint}
          />
        </div>
      </div>
    </div>
  );
}

export default MultiUploadDropzone;
